import { motion } from "motion/react";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { CollapsableContainer } from "@ryde/controls";
import { StationRouteContext, useRtl } from "@ryde/redux";
import {
  COLLAPSE_DURATION_SEC,
  formatMatchingMlPathHeaderValue,
  onActivateKeyDown,
  stationRouteMatchingMlPathIdsAtom,
  stationRouteMatchingMlPathsExpandedAtom,
  stationRouteMatchingMlPathUpdateIdAtom,
} from "@ryde/shared";

import { RideOpenStationRouteLink } from "../../../drawers/components/ride-edit-form/components/ride-open-station-route-link";
import {
  StationRouteMatchingMlPathExpandToggleStyled,
  StationRouteMatchingMlPathHeaderStyled,
  StationRouteMatchingMlPathsStyled,
} from "./station-route.styled";
import { useStationRouteMatchingMlPathsSync } from "./use-station-route-matching-ml-paths-sync";

const MotionArrowStatExpand = motion.create(SVG_MUI.ArrowStatExpand3SVG);

export const StationRouteMatchingMlPaths: FC = () => {
  const { t } = useTranslation();
  const { isRtl } = useRtl();
  const { rideOpenerId } = useContext(StationRouteContext);
  useStationRouteMatchingMlPathsSync();

  const matchingIds = useRecoilValue(stationRouteMatchingMlPathIdsAtom);
  const updateId = useRecoilValue(stationRouteMatchingMlPathUpdateIdAtom);
  const [isExpanded, setIsExpanded] = useRecoilState(
    stationRouteMatchingMlPathsExpandedAtom,
  );

  if (!rideOpenerId) {
    return null;
  }

  const headerValue = formatMatchingMlPathHeaderValue({
    updateId,
    count: matchingIds.length,
    newLabel: t("stationRoute.formLabel.matchingMlPathNew"),
  });
  const collapsedRotation = isRtl ? 180 : 0;
  const canExpand = matchingIds.length > 1;
  const rideDto = { id: rideOpenerId };

  return (
    <StationRouteMatchingMlPathsStyled data-e2e="stationRoute-matching-ml-paths">
      <StationRouteMatchingMlPathHeaderStyled>
        <label>{t("stationRoute.single")}:</label>
        {updateId ? (
          <RideOpenStationRouteLink
            stationRouteIds={[updateId]}
            rideDto={rideDto}
          />
        ) : (
          <span>{headerValue}</span>
        )}
        {matchingIds.length > 1 && (
          <span data-e2e="stationRoute-matching-ml-paths-count">
            ({matchingIds.length})
          </span>
        )}
        {canExpand && (
          <StationRouteMatchingMlPathExpandToggleStyled
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? t("stationRoute.formLabel.matchingMlPathsCollapse")
                : t("stationRoute.formLabel.matchingMlPathsExpand")
            }
            onClick={() => setIsExpanded(prev => !prev)}
            onKeyDown={onActivateKeyDown(() => setIsExpanded(prev => !prev))}
          >
            <MotionArrowStatExpand
              animate={{ rotate: isExpanded ? 90 : collapsedRotation }}
              transition={{ duration: COLLAPSE_DURATION_SEC }}
            />
          </StationRouteMatchingMlPathExpandToggleStyled>
        )}
      </StationRouteMatchingMlPathHeaderStyled>
      {canExpand && (
        <CollapsableContainer isCollapsed={isExpanded} isSkipMaxHeight>
          <RideOpenStationRouteLink
            stationRouteIds={matchingIds}
            rideDto={rideDto}
          />
        </CollapsableContainer>
      )}
    </StationRouteMatchingMlPathsStyled>
  );
};
